// El listado de lo fiado, venta por venta.
//
// ── El problema que hay que resolver ──
//
// Los abonos NO se anotan contra una venta: se le abonan al cliente. Si Pedro
// debe tres despachos y trae $500.000, esa plata entra al montón. Por eso la
// pantalla de Clientes solo puede decir un saldo por persona.
//
// Pero cobrar no se hace por saldos, se hace por despachos: «el del 4 de junio
// todavía no me lo ha pagado». Para poder decir eso hay que repartir los abonos
// entre las ventas, y aquí se reparten del MÁS VIEJO AL MÁS NUEVO.
//
// Se reparte así porque es como cobra todo el mundo: la plata que entra tapa lo
// que más lleva debiendo. Repartirla a prorrata dejaría todas las ventas
// pagadas a medias y ninguna cerrada, que no es como nadie lleva una cuenta.
//
// Ojo con lo que esto SÍ y NO es: el total que debe cada cliente es exacto
// —salga como salga el reparto, la suma no cambia—. Lo que es una manera de
// leerlo es CUÁL venta quedó pendiente. Si el cliente dice «yo le abone el del
// 4», la plata es la misma; solo cambia el renglón.

const soloFecha = (f) => String(f || '').slice(0, 10);

// Días enteros entre dos fechas, comparando días y no instantes. Restar fechas
// con hora hace que un despacho de anoche salga como «hace 0 días» o «hace 1»
// según a qué hora se abra la pantalla.
export function diasDesde(fecha, hoy) {
  const a = new Date(soloFecha(fecha) + 'T00:00:00');
  const b = hoy ? new Date(soloFecha(hoy) + 'T00:00:00')
                : new Date(new Date().toDateString());
  if (isNaN(a) || isNaN(b)) return null;
  return Math.floor((b - a) / 86400000);
}

// Menos de un peso de saldo es una venta pagada: son los centavos que deja un
// abono redondeado. Un renglón de «debe $0» en una lista de cobros es ruido.
const NADA = 1;

// Cada venta fiada con lo que le queda debiendo, de la más vieja a la más nueva.
//
//   ventas  [{ id, fecha, total, cliente_id, cliente, … }] — solo las de crédito
//           y sin anular. Quien llama filtra: aquí no se adivina.
//   abonos  [{ cliente_id, abonado }] — el total abonado por cada cliente.
export function creditosPendientes(ventas, abonos, hoy) {
  const bolsa = new Map();
  for (const a of abonos || []) {
    bolsa.set(a.cliente_id, (bolsa.get(a.cliente_id) || 0) + (Number(a.abonado) || 0));
  }

  // De la más vieja a la más nueva. Con la misma fecha manda el id, que es el
  // orden en que se anotaron: sin ese desempate el reparto cambiaría de una
  // carga a otra y la misma venta saldría pendiente unas veces sí y otras no.
  const ordenadas = [...(ventas || [])].sort((x, y) =>
    soloFecha(x.fecha).localeCompare(soloFecha(y.fecha)) || (x.id - y.id));

  const salida = [];
  for (const v of ordenadas) {
    const total = Number(v.total) || 0;
    const hay = bolsa.get(v.cliente_id) || 0;
    const cubre = Math.min(hay, total);
    bolsa.set(v.cliente_id, hay - cubre);

    const saldo = total - cubre;
    if (saldo < NADA) continue;                 // ya está pagada

    salida.push({
      ...v,
      total,
      abonado: cubre,
      saldo,
      // Pagada a medias: se dice, porque no es lo mismo que no haber abonado
      // nada. Un cliente que viene abonando no se cobra igual que uno que no.
      parcial: cubre > 0,
      dias: diasDesde(v.fecha, hoy),
    });
  }
  return salida;
}

// Lo que sobró de los abonos después de tapar todo lo fiado: plata a favor del
// cliente. Pasa al anular una venta que ya se había abonado.
//
// Se devuelve aparte y no como un saldo negativo: escribir «−$300.000 debe» en
// una lista de cobros se lee como un error del programa.
export function aFavor(ventas, abonos) {
  const debido = new Map();
  for (const v of ventas || []) {
    debido.set(v.cliente_id, (debido.get(v.cliente_id) || 0) + (Number(v.total) || 0));
  }
  const sobras = [];
  for (const a of abonos || []) {
    const sobra = (Number(a.abonado) || 0) - (debido.get(a.cliente_id) || 0);
    if (sobra >= NADA) sobras.push({ cliente_id: a.cliente_id, monto: sobra });
  }
  return sobras;
}
